/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
    Component,
    EventEmitter,
    Output,
    OnInit,
    OnChanges,
    Input,
    SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
    AuctionCatalogsService,
    BasicListResponse,
    ListResponse,
    LotDetail,
    NotificationsService,
    Reminder,
} from '@viewbid/ts-angular-client';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { VbSnackbarService } from '@viewbid/ui';
import { SchedulerDialogComponent } from 'src/app/viewbid/shared/components/scheduler-dialog/scheduler-dialog.component';
import { SocketService } from 'src/app/viewbid/shared/services/socket.service';
import { ServerClockService } from 'src/app/viewbid/shared/services/server-clock.service';

@Component({
    selector: 'vb-lot-item-alerts',
    templateUrl: './lot-item-alerts.component.html',
    styleUrls: ['./lot-item-alerts.component.scss'],
})
export class LotItemAlertsComponent implements OnInit, OnChanges {
    @Output() closeReminders: EventEmitter<void> = new EventEmitter<void>();
    @Input() lot!: LotDetail;
    @Output() remindersChange: EventEmitter<Reminder[]> = new EventEmitter<
        Reminder[]
    >();

    public reminders: Reminder[] = [];
    public isEditing: boolean = false;
    public isAdding: boolean = false;
    public formValid: boolean = false;
    public currentPrice: number | undefined = undefined;
    public alertTimes: any[] = [];
    /**
     * Every alert time, each tagged with `unavailable` — the ones already
     * past or already spoken for stay on screen, crossed out, so the full
     * set of choices reads at a glance.
     */
    public alertTimeOptions: any[] = [];
    public allTimesUsed = false;
    public isLoading: boolean = true;
    private successToastId: number = 0;
    private toDatetime: string = '';

    public currentReminder: Reminder = {
        notifyAtLabel: undefined,
        email: false,
        sms: false,
    };
    public originalReminder: Reminder = {
        notifyAtLabel: undefined,
        email: false,
        sms: false,
    };

    constructor(
        private notificationsService: NotificationsService,
        private auctionService: AuctionCatalogsService,
        public bsModalRef: BsModalRef,
        private socketService: SocketService,
        private snackbarService: VbSnackbarService,
        private translate: TranslateService,
        public schedulerDialogComponent: SchedulerDialogComponent,
        private serverClock: ServerClockService,
    ) {
        this.socketService.resource$.subscribe((data: string) => {
            const eventResponse: any = JSON.parse(data);
            if (
                eventResponse.action === 'PAGE_LOAD' &&
                eventResponse.data.auctionLotsId === this.lot.auctionLotsId
            ) {
                this.currentPrice = eventResponse.data.currentPrice;
            }
        });
    }

    ngOnInit(): void {
        this.resetCurrentReminder();

        this.currentPrice = this.lot.currentPrice;
        this.toDatetime = this.lot.toDatetime ?? '';

        this.auctionService.getListByGroup('reminders').subscribe({
            next: (res: ListResponse) => {
                const basicList: BasicListResponse[] =
                    res.data as BasicListResponse[];
                if (res.data.length > 0) {
                    this.prepareAlertTimes(basicList);
                }
                this.getReminders();
            },
            error: () => {
                this.isLoading = false;
            },
        });
        this.checkFormValid();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['lot'] && !changes['lot'].firstChange) {
            this.toDatetime = this.lot.toDatetime ?? '';
            this.filterAlertTimes();
            this.checkFormValid();
        }
    }

    prepareAlertTimes(response: BasicListResponse[]): void {
        response.forEach((item: BasicListResponse) => {
            this.alertTimes.push({
                value: item.value.toString(),
                text: `${this.convertMillisecondsToTime(item.value)} before the auction closes`,
            });
        });
    }

    saveReminder(): void {
        if (!this.isNotifyAtValid(this.currentReminder.notifyAtLabel)) {
            this.snackbarService.error(
                this.translate.instant(
                    'Notifications.errorMessageReminderTimePassed',
                ),
            );
            this.resetCurrentReminder();
            this.filterAlertTimes();
            return;
        }

        this.notificationsService
            .createAuctionReminder({
                reminderTypeId: 3,
                notifyAt: new Date(
                    new Date(this.toDatetime).getTime() -
                        Number(this.currentReminder.notifyAtLabel),
                ).toISOString(),
                notifyAtLabel: this.currentReminder.notifyAtLabel!,
                notificationSent: false,
                proxyId: this.lot.auctionLotsId!,
                sms: this.currentReminder.sms ?? false,
                email: this.currentReminder.email ?? false,
            })
            .subscribe({
                next: () => {
                    this.snackbarService.dismiss(this.successToastId);
                    this.successToastId = this.snackbarService.success(
                        this.translate.instant(
                            'Notifications.successReminderSet',
                        ),
                    );
                    this.getReminders();
                },
                error: () => {
                    this.snackbarService.error(
                        this.translate.instant(
                            'Notifications.errorReminderSet',
                        ),
                    );
                    this.getReminders();
                },
            });
        this.resetCurrentReminder();
        this.isEditing = false;
        this.filterAlertTimes();
        this.sortReminders();
    }

    getReminders(): void {
        this.reminders = [];
        this.notificationsService
            .getRemindersByAccountMappingsId(
                1,
                -1,
                this.lot.auctionLotsId ?? undefined,
            )
            .subscribe({
                next: (res: ListResponse) => {
                    if (res.data && res.data.length > 0) {
                        res.data.forEach((reminder: Reminder) => {
                            this.reminders.push(reminder);
                        });
                    }
                    this.filterAlertTimes();
                    this.sortReminders();
                    this.remindersChange.emit(this.reminders);
                    this.isLoading = false;
                },
                error: () => {
                    this.isLoading = false;
                },
            });
    }

    updateReminder(): void {
        if (!this.isNotifyAtValid(this.currentReminder.notifyAtLabel)) {
            this.snackbarService.error(
                this.translate.instant(
                    'Notifications.errorMessageReminderTimePassed',
                ),
            );
            this.resetCurrentReminder();
            this.resetOriginalReminder();
            this.filterAlertTimes();
            this.isEditing = false;
            return;
        }

        this.notificationsService
            .putReminderById(this.currentReminder.id!, {
                reminderTypeId: 3,
                notifyAt: new Date(
                    new Date(this.toDatetime).getTime() -
                        Number(this.currentReminder.notifyAtLabel),
                ).toISOString(),
                notifyAtLabel: this.currentReminder.notifyAtLabel!,
                notificationSent: false,
                proxyId: this.lot.auctionLotsId!,
                sms: this.currentReminder.sms,
                email: this.currentReminder.email,
            })
            .subscribe({
                next: () => {
                    this.snackbarService.dismiss(this.successToastId);
                    this.successToastId = this.snackbarService.success(
                        this.translate.instant(
                            'Notifications.successReminderUpdated',
                        ),
                    );
                    this.getReminders();
                },
                error: () => {
                    this.snackbarService.error(
                        this.translate.instant(
                            'Notifications.errorReminderUpdated',
                        ),
                    );
                    this.getReminders();
                },
            });

        this.resetCurrentReminder();
        this.resetOriginalReminder();
        this.filterAlertTimes();
        this.isEditing = false;
    }

    deleteReminder(reminder: Reminder): void {
        this.notificationsService.deleteReminderById(reminder.id!).subscribe({
            next: () => {
                this.snackbarService.dismiss(this.successToastId);
                this.successToastId = this.snackbarService.success(
                    this.translate.instant(
                        'Notifications.successReminderDeleted',
                    ),
                );
                this.getReminders();
                this.filterAlertTimes();
            },
            error: () => {
                this.snackbarService.error(
                    this.translate.instant(
                        'Notifications.errorReminderDeleted',
                    ),
                );
                this.getReminders();
            },
        });
    }

    resetCurrentReminder(): void {
        this.currentReminder = {
            notifyAtLabel: undefined,
            email: true,
            sms: true,
        };
        this.checkFormValid();
    }

    resetOriginalReminder(): void {
        this.originalReminder = {
            notifyAtLabel: undefined,
            email: true,
            sms: true,
        };
        this.checkFormValid();
    }

    editReminder(reminder: Reminder): void {
        this.currentReminder = { ...reminder };
        this.originalReminder = { ...reminder };
        this.isEditing = true;

        this.filterAlertTimes();
        this.checkFormValid();
    }

    cancelEdit(): void {
        this.resetCurrentReminder();
        this.resetOriginalReminder();
        this.isEditing = false;
        this.filterAlertTimes();
    }
    convertMillisecondsToTime(value: string): string {
        return this.schedulerDialogComponent.convertMillisecondsToTime(
            Number(value),
        );
    }

    filterAlertTimes(): void {
        const timeUntilClose: number =
            this.getTimeUntilAuctionClosesInMilliseconds();

        // A time is unavailable when it has already passed, or when an
        // existing alert already claims it (the one being edited excepted).
        // Unavailable times are kept in the list and rendered struck through
        // rather than dropped, so the chips don't shuffle as time runs out.
        this.alertTimeOptions = this.alertTimes.map((time: any) => ({
            ...time,
            unavailable:
                Number(time.value) >= timeUntilClose ||
                this.reminders.some(
                    (reminder: Reminder) =>
                        reminder.notifyAtLabel === time.value &&
                        !(
                            this.isEditing &&
                            reminder.notifyAtLabel ===
                                this.originalReminder.notifyAtLabel
                        ),
                ),
        }));

        this.allTimesUsed = this.alertTimeOptions.every(
            (time: any) => time.unavailable,
        );
    }

    getTimeUntilAuctionClosesInMilliseconds(): number {
        const auctionEndTime: number = new Date(this.toDatetime).getTime();
        const currentTime: number = this.serverClock.now();
        const timeDifferenceInMilliseconds: number =
            auctionEndTime - currentTime;
        return timeDifferenceInMilliseconds;
    }

    isNotifyAtValid(notifyAtLabel: string | undefined): boolean {
        return (
            !!notifyAtLabel &&
            Number(notifyAtLabel) <
                this.getTimeUntilAuctionClosesInMilliseconds()
        );
    }

    checkFormValid(): void {
        this.formValid =
            !!this.currentReminder.notifyAtLabel &&
            this.isNotifyAtValid(this.currentReminder.notifyAtLabel) &&
            ((this.currentReminder.email ?? false) ||
                (this.currentReminder.sms ?? false)) &&
            !this.isSameAsOriginal();
    }

    selectAlertTime(value: string): void {
        this.currentReminder.notifyAtLabel = value;
        this.checkFormValid();
    }

    toggleMethod(method: 'email' | 'sms'): void {
        this.currentReminder[method] = !this.currentReminder[method];
        this.checkFormValid();
    }

    methodLabel(reminder: Reminder): string {
        if (reminder.email && reminder.sms) return 'Email and text';
        return reminder.email ? 'Email' : 'Text';
    }

    isSameAsOriginal(): boolean {
        if (!this.originalReminder) {
            return false;
        }

        return (
            this.currentReminder.notifyAtLabel ===
                this.originalReminder.notifyAtLabel &&
            this.currentReminder.email === this.originalReminder.email &&
            this.currentReminder.sms === this.originalReminder.sms
        );
    }

    getNotificationText(): string {
        const parts: any[] = [];
        if (this.currentReminder.sms) {
            parts.push('<strong>text</strong>');
        }
        if (this.currentReminder.email) {
            parts.push('<strong>email</strong>');
        }
        const notifyAtText: string =
            this.alertTimes.find(
                (option: any) =>
                    option.value === this.currentReminder.notifyAtLabel,
            )?.text || '';
        return `We'll send you a ${parts.join(' and ')} notification <strong>${notifyAtText.toLowerCase()}</strong> to notify you of the item's price.`;
    }

    sortReminders(): void {
        this.reminders.sort((a: Reminder, b: Reminder) => {
            const aValue: number = parseInt(a.notifyAtLabel || '0', 10);
            const bValue: number = parseInt(b.notifyAtLabel || '0', 10);
            return aValue - bValue;
        });
    }

    closeComponent(): void {
        this.closeReminders.emit();
    }
}
